import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { faCheckCircle, faClock, faDatabase, faExclamationTriangle, faTimesCircle, faUserCog } from '@fortawesome/free-solid-svg-icons';
import { faChartBar } from '@fortawesome/free-regular-svg-icons';
import { HeaderActionsComponent, HeaderActionsConfig } from '../../components/header-actions/header-actions.component';
import { DashboardService, DashboardMetrics, DashboardConfigUsageItem, ModelSeriesPoint } from '../../services/dashboard.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { Router } from '@angular/router';
import { TenantService } from '../../services/tenant.service';
import { UsersService } from '../../services/users.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { ButtonComponent } from '../../components/button/button.component';
import { Chart } from 'chart.js/auto';

interface ScopeOption {
  id: string;
  name: string;
  type: 'all' | 'user';
}

interface ModelTypeOption {
  id: 'all' | 'extraction' | 'classification';
  name: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule, HeaderActionsComponent, NgSelectModule, PaginationComponent, ButtonComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  protected readonly Math = Math;
  metrics: DashboardMetrics | null = null;
  loading = false;
  usersLoading = false;
  private themeObserver?: MutationObserver;
  modelSeries: ModelSeriesPoint[] = [];

  @ViewChild('statusChartCanvas')
  set statusChartCanvasRef(value: ElementRef<HTMLCanvasElement> | undefined) {
    this.statusChartCanvas = value;
    if (value) {
      this.renderStatusChart();
    }
  }
  private statusChartCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('statusLegend')
  private statusLegend?: ElementRef<HTMLDivElement>;
  private statusChart?: Chart;

  @ViewChild('modelsSeriesChartCanvas')
  set modelsSeriesChartCanvasRef(value: ElementRef<HTMLCanvasElement> | undefined) {
    this.modelsSeriesChartCanvas = value;
    if (value) {
      this.renderModelsSeriesChart();
    }
  }
  private modelsSeriesChartCanvas?: ElementRef<HTMLCanvasElement>;
  private modelsSeriesChart?: Chart;

  @ViewChild('fileTypesChartCanvas')
  set fileTypesChartCanvasRef(value: ElementRef<HTMLCanvasElement> | undefined) {
    this.fileTypesChartCanvas = value;
    if (value) {
      this.renderFileTypesChart();
    }
  }
  private fileTypesChartCanvas?: ElementRef<HTMLCanvasElement>;
  private fileTypesChart?: Chart;

  readonly isAdmin = sessionStorage.getItem('role') === 'ADMIN';

  selectedScopeOption = 'all';
  scopeOptions: ScopeOption[] = [];

  selectedModelType: 'all' | 'extraction' | 'classification' = 'all';
  modelTypeOptions: ModelTypeOption[] = [];

  startDate = '';
  endDate = '';

  topSearchTerm = '';
  topPage = 1;
  readonly topPageSize = 5;

  readonly scanLimit = 2000;
  readonly minDate: string;
  readonly maxDate: string;

  headerConfig: HeaderActionsConfig = {
    showBackButton: false,
    showAddButton: false,
    showSearch: false,
    showTagFilter: false,
  };

  faDatabase = faDatabase;
  faChartBar = faChartBar;
  faCheckCircle = faCheckCircle;
  faClock = faClock;
  faTimesCircle = faTimesCircle;
  faExclamationTriangle = faExclamationTriangle;
  faUserCog = faUserCog;

  constructor(
    private dashboardService: DashboardService,
    private usersService: UsersService,
    private alertService: AlertService,
    private translate: TranslateService,
    private router: Router,
    private tenantService: TenantService
  ) {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    this.maxDate = this.toDateInputValue(today);
    this.minDate = this.toDateInputValue(oneYearAgo);
    this.startDate = this.toDateInputValue(sevenDaysAgo);
    this.endDate = this.toDateInputValue(today);
  }

  ngOnInit(): void {
    if (!this.isAdmin) {
      this.alertService.showError(this.translate.instant('DASHBOARD.ERROR.ADMIN_ONLY'));
      this.router.navigate(this.tenantService.buildRoute('/settings'));
      return;
    }

    this.setupThemeObserver();
    this.loadScopeOptions();
    this.loadModelTypeOptions();
    this.loadMetrics();
  }

  ngOnDestroy(): void {
    this.destroyStatusChart();
    this.destroyModelsSeriesChart();
    this.destroyFileTypesChart();
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = undefined;
    }
  }

  onBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/settings'));
  }

  onScopeSelectionChange(selection: unknown): void {
    if (typeof selection === 'string' && selection.trim() !== '') {
      this.selectedScopeOption = selection;
    } else {
      this.selectedScopeOption = 'all';
    }
    this.topPage = 1;
    this.loadMetrics();
  }

  onModelTypeSelectionChange(selection: unknown): void {
    if (selection === 'extraction' || selection === 'classification') {
      this.selectedModelType = selection;
    } else {
      this.selectedModelType = 'all';
    }
    this.topPage = 1;
    this.loadMetrics();
  }

  onApplyCustomPeriod(): void {
    if (!this.validateAndNormalizePeriod()) {
      return;
    }
    this.topPage = 1;
    this.loadMetrics();
  }

  onTopSearchChange(value: string): void {
    this.topSearchTerm = value;
    this.topPage = 1;
    this.loadMetrics();
  }

  onTopPageChange(page: number): void {
    this.topPage = page;
    this.loadMetrics();
  }

  loadMetrics(): void {
    if (!this.isAdmin) {
      return;
    }

    if (!this.validateAndNormalizePeriod()) {
      return;
    }

    this.loading = true;

    const selectedScope = this.scopeOptions.find(option => option.id === this.selectedScopeOption);
    const scope: 'all' | 'user' = selectedScope?.type === 'user' ? 'user' : 'all';
    const targetUserId = selectedScope?.type === 'user' ? selectedScope.id : null;

    this.dashboardService
      .getMetrics({
        scope,
        targetUserId,
        modelType: this.selectedModelType,
        startDate: this.startDate || null,
        endDate: this.endDate || null,
        limit: this.scanLimit,
        topSearch: this.topSearchTerm,
        topPage: this.topPage,
        topPageSize: this.topPageSize,
      })
      .subscribe({
        next: (response) => {
          this.metrics = response.metrics;
          this.topPage = response.metrics.top_interactions_page || 1;
          this.renderStatusChart();
          this.loadModelsSeries();
          this.renderFileTypesChart();
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.alertService.showError(err);
        }
      });
  }

  private loadScopeOptions(): void {
    this.usersLoading = true;

    this.usersService.list().subscribe({
      next: (response) => {
        const previousSelection = this.selectedScopeOption;
        this.scopeOptions = [
          { id: 'all', name: this.translate.instant('DASHBOARD.SCOPE.ALL'), type: 'all' },
          ...response.users.map((user) => ({
            id: user.id,
            name: `${user.username} (${user.email})`,
            type: 'user' as const
          }))
        ];

        const hasPreviousSelection = this.scopeOptions.some(option => option.id === previousSelection);
        this.selectedScopeOption = hasPreviousSelection ? previousSelection : 'all';

        this.usersLoading = false;
      },
      error: (err) => {
        this.usersLoading = false;
        this.scopeOptions = [{ id: 'all', name: this.translate.instant('DASHBOARD.SCOPE.ALL'), type: 'all' }];
        this.alertService.showError(err);
      }
    });
  }

  private loadModelTypeOptions(): void {
    this.modelTypeOptions = [
      { id: 'all', name: this.translate.instant('DASHBOARD.MODEL_TYPE.ALL') },
      { id: 'extraction', name: this.translate.instant('DASHBOARD.MODEL_TYPE.EXTRACTION') },
      { id: 'classification', name: this.translate.instant('DASHBOARD.MODEL_TYPE.CLASSIFICATION') },
    ];
  }

  private validateAndNormalizePeriod(): boolean {
    if (!this.startDate || !this.endDate) {
      this.alertService.showError(this.translate.instant('DASHBOARD.ERROR.DATE_RANGE_REQUIRED'));
      return false;
    }

    const start = new Date(`${this.startDate}T00:00:00`);
    const end = new Date(`${this.endDate}T00:00:00`);

    if (start > end) {
      this.alertService.showError(this.translate.instant('DASHBOARD.ERROR.INVALID_DATE_RANGE'));
      return false;
    }

    const maxSpanInMs = 365 * 24 * 60 * 60 * 1000;
    if (end.getTime() - start.getTime() > maxSpanInMs) {
      this.alertService.showError(this.translate.instant('DASHBOARD.ERROR.MAX_PERIOD_EXCEEDED'));
      return false;
    }

    return true;
  }

  private toDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getConfigUsage(configField: keyof DashboardMetrics['top_configurations']): DashboardConfigUsageItem[] {
    if (!this.metrics) {
      return [];
    }
    return this.metrics.top_configurations[configField] || [];
  }

  private renderStatusChart(): void {
    if (!this.metrics || !this.statusChartCanvas) {
      return;
    }

    const labels = [
      this.translate.instant('INTERACTIONS.STATUS.PENDING'),
      this.translate.instant('INTERACTIONS.STATUS.COMPLETED'),
      this.translate.instant('INTERACTIONS.STATUS.FAILED'),
      this.translate.instant('INTERACTIONS.STATUS.CANCELLED')
    ];

    const data = [
      this.metrics.status_counts.pending,
      this.metrics.status_counts.completed,
      this.metrics.status_counts.failed,
      this.metrics.status_counts.cancelled
    ];

    this.destroyStatusChart();

    const ctx = this.statusChartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }

    const total = data.reduce((sum, v) => sum + v, 0);
    const rootStyle = getComputedStyle(document.documentElement);
    const borderColor = rootStyle.getPropertyValue('--border-color')?.trim() || '#232323';
    const colors = [
      rootStyle.getPropertyValue('--status-pending-color')?.trim() || '#8f9aa8',
      rootStyle.getPropertyValue('--status-completed-color')?.trim() || '#24c58c',
      rootStyle.getPropertyValue('--status-failed-color')?.trim() || '#f05d5e',
      rootStyle.getPropertyValue('--status-cancelled-color')?.trim() || '#f0b429',
    ];

    const htmlLegendPlugin = {
      id: 'htmlLegend',
      afterUpdate: (chart: Chart) => {
        const legendContainer = this.statusLegend?.nativeElement;
        if (!legendContainer) {
          return;
        }

        legendContainer.innerHTML = '';
        const list = document.createElement('ul');
        list.className = 'chartjs-legend';
        legendContainer.appendChild(list);

        const items = chart.options.plugins?.legend?.labels?.generateLabels
          ? chart.options.plugins.legend.labels.generateLabels(chart)
          : Chart.defaults.plugins.legend.labels.generateLabels(chart);

        items.forEach((item) => {
          const li = document.createElement('li');
          if (item.hidden) {
            li.classList.add('hidden');
          } else {
            li.classList.add('visible');
          }

          li.onclick = () => {
            chart.toggleDataVisibility(item.index as number);
            chart.update();
          };

          const box = document.createElement('span');
          box.className = 'legend-color';
          box.style.background = item.fillStyle as string;
          box.style.borderColor = item.strokeStyle as string;

          const text = document.createElement('span');
          text.textContent = item.text;

          li.appendChild(box);
          li.appendChild(text);
          list.appendChild(li);
        });
      }
    };
    this.statusChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: colors,
            borderColor,
            borderWidth: 2,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1,
        plugins: {
          legend: {
            display: false,
            labels: {
              boxWidth: 18,
              boxHeight: 18,
              padding: 16,
              font: {
                size: 19,
                family: 'inherit',
                weight: 'bold'
              },
              generateLabels: (chart) => {
                const ds = chart.data.datasets[0];
                return chart.data.labels!.map((label, i) => {
                  const value = ds.data[i] as number;
                  const hidden = !chart.getDataVisibility(i);
                  return {
                    text: `${label} (${value})`,
                    fillStyle: ds.backgroundColor ? (Array.isArray(ds.backgroundColor) ? ds.backgroundColor[i] : ds.backgroundColor) : undefined,
                    strokeStyle: ds.borderColor ? (Array.isArray(ds.borderColor) ? ds.borderColor[i] : ds.borderColor) : undefined,
                    lineWidth: ds.borderWidth as number,
                    hidden,
                    index: i
                  };
                });
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = context.parsed;
                const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return `${context.label}: ${value} (${percent}%)`;
              }
            }
          }
        }
      },
      plugins: [htmlLegendPlugin]
    });
  }

  private loadModelsSeries(): void {
    if (!this.metrics) {
      return;
    }

    const selectedScope = this.scopeOptions.find(option => option.id === this.selectedScopeOption);
    const scope: 'all' | 'user' = selectedScope?.type === 'user' ? 'user' : 'all';
    const targetUserId = selectedScope?.type === 'user' ? selectedScope.id : null;

    this.dashboardService
      .getModelsSeries({
        scope,
        targetUserId,
        startDate: this.startDate || null,
        endDate: this.endDate || null,
      })
      .subscribe({
        next: (response) => {
          this.modelSeries = response.series.points;
          this.renderModelsSeriesChart();
        },
        error: (err) => {
          this.alertService.showError(err);
        }
      });
  }

  private renderModelsSeriesChart(): void {
    if (!this.modelsSeriesChartCanvas) {
      return;
    }

    this.destroyModelsSeriesChart();

    const ctx = this.modelsSeriesChartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }

    const labels: string[] = [];
    const extractionCounts: number[] = [];
    const classificationCounts: number[] = [];

    const start = this.startDate ? new Date(`${this.startDate}T00:00:00`) : null;
    const end = this.endDate ? new Date(`${this.endDate}T00:00:00`) : null;
    const extractionMap = new Map(
      this.modelSeries.filter(point => point.model_type === 'extraction').map(point => [point.date, point.count])
    );
    const classificationMap = new Map(
      this.modelSeries.filter(point => point.model_type === 'classification').map(point => [point.date, point.count])
    );

    if (start && end) {
      const cursor = new Date(start);
      while (cursor <= end) {
        const key = cursor.toISOString().slice(0, 10);
        labels.push(key);
        extractionCounts.push(extractionMap.get(key) ?? 0);
        classificationCounts.push(classificationMap.get(key) ?? 0);
        cursor.setDate(cursor.getDate() + 1);
      }
    } else {
      const allDates = Array.from(new Set(this.modelSeries.map(point => point.date))).sort();
      allDates.forEach(dateKey => {
        labels.push(dateKey);
        extractionCounts.push(extractionMap.get(dateKey) ?? 0);
        classificationCounts.push(classificationMap.get(dateKey) ?? 0);
      });
    }

    const rootStyle = getComputedStyle(document.documentElement);
    const axisColor = rootStyle.getPropertyValue('--text-secondary')?.trim() || '#94a3b8';
    const gridColor = rootStyle.getPropertyValue('--border-color')?.trim() || '#2a2a2a';
    const extractionColor = rootStyle.getPropertyValue('--accent-color')?.trim() || '#24c58c';
    const classificationColor = '#4f8cff';

    this.modelsSeriesChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: this.translate.instant('DASHBOARD.CHARTS.MODELS_CREATED_EXTRACTION'),
            data: extractionCounts,
            borderColor: extractionColor,
            backgroundColor: 'rgba(36, 197, 140, 0.15)',
            pointRadius: 3,
            tension: 0.3,
            fill: true,
          },
          {
            label: this.translate.instant('DASHBOARD.CHARTS.MODELS_CREATED_CLASSIFICATION'),
            data: classificationCounts,
            borderColor: classificationColor,
            backgroundColor: 'rgba(79, 140, 255, 0.15)',
            pointRadius: 3,
            tension: 0.3,
            fill: true,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            ticks: {
              color: axisColor,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8,
            },
            grid: {
              color: gridColor,
            }
          },
          y: {
            ticks: {
              color: axisColor,
              precision: 0,
            },
            grid: {
              color: gridColor,
            },
            beginAtZero: true,
          }
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              color: axisColor,
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${context.parsed.y}`
            }
          }
        }
      }
    });
  }

  private renderFileTypesChart(): void {
    if (!this.metrics || !this.fileTypesChartCanvas) {
      return;
    }

    this.destroyFileTypesChart();

    const ctx = this.fileTypesChartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }

    const labels = this.metrics.file_types.map(item => item.extension);
    const counts = this.metrics.file_types.map(item => item.count);
    const colors = [
      '#24c58c',
      '#4f8cff',
      '#f05d5e',
      '#f0b429',
      '#a855f7',
      '#22c1c3',
      '#94a3b8',
    ];

    const rootStyle = getComputedStyle(document.documentElement);
    const axisColor = rootStyle.getPropertyValue('--text-secondary')?.trim() || '#94a3b8';
    const gridColor = rootStyle.getPropertyValue('--border-color')?.trim() || '#2a2a2a';

    this.fileTypesChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: this.translate.instant('DASHBOARD.CHARTS.FILE_TYPES'),
            data: counts,
            backgroundColor: labels.map((_, idx) => colors[idx % colors.length]),
            borderRadius: 6,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            ticks: {
              color: axisColor,
            },
            grid: {
              display: false,
            }
          },
          y: {
            ticks: {
              color: axisColor,
              precision: 0,
            },
            grid: {
              color: gridColor,
            },
            beginAtZero: true,
          }
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => `${context.parsed.y}`
            }
          }
        }
      }
    });
  }

  private destroyFileTypesChart(): void {
    if (this.fileTypesChart) {
      this.fileTypesChart.destroy();
      this.fileTypesChart = undefined;
    }
  }

  private destroyModelsSeriesChart(): void {
    if (this.modelsSeriesChart) {
      this.modelsSeriesChart.destroy();
      this.modelsSeriesChart = undefined;
    }
  }

  private setupThemeObserver(): void {
    if (this.themeObserver) {
      return;
    }

    this.themeObserver = new MutationObserver(() => {
      this.renderStatusChart();
      this.renderModelsSeriesChart();
      this.renderFileTypesChart();
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'style']
    });
  }

  private destroyStatusChart(): void {
    if (this.statusChart) {
      this.statusChart.destroy();
      this.statusChart = undefined;
    }
  }
}
